import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

// Types/Interfaces
import { INotification } from "@/Common/api/types/notifications";
import { INotificationSubscription } from "./interfaces";
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";

// Queries
import {
	onCreateNotificationSubscription,
	onUpdateNewNotificationSubscription,
	onUpdateNotificationSubscription,
} from "@/Common/api/notifications/subscriptionQueries";

// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Handlers
import {
	onCreateNotificationHandler,
	onUpdateNewNotificationHandler,
	onUpdateNotificationHandler,
} from "@/Common/api/notifications/subscriptionHandlers";

export const useNotificationSubscriptions = (props: Readonly<INotificationSubscription>) => {
	const { userId = "", locationId = "" } = props || {};

	const queryClient = useQueryClient();
	const { getSubscription, addSubscription, deleteSubscription } = useSubscriptions();

	const subscription = getSubscription(["notifications", userId]);

	subscription?.subscriptions?.onUpdateNewNotification?.setCallback(
		(data: GraphqlQueryResponse<"onUpdateNewNotification", unknown>["data"]) => {
			const notification = data.data?.onUpdateNewNotification;
			if (!userId || !locationId || !notification) {
				return;
			}
			onUpdateNewNotificationHandler(queryClient, userId, locationId);
		}
	);

	subscription?.subscriptions?.onCreateNotification?.setCallback(
		(data: GraphqlQueryResponse<"onCreateNotification", INotification>["data"]) => {
			const notification = data.data?.onCreateNotification;
			if (!userId || !locationId || !notification) {
				return;
			}
			onCreateNotificationHandler(queryClient, userId, locationId, notification);
		}
	);

	subscription?.subscriptions?.onUpdateNotification?.setCallback(
		(data: GraphqlQueryResponse<"onUpdateNotification", INotification>["data"]) => {
			const notification = data.data?.onUpdateNotification;
			if (!userId || !locationId || !notification) {
				return;
			}
			onUpdateNotificationHandler(queryClient, userId, locationId, notification);
		}
	);

	useEffect(() => {
		const identifier = ["notifications", userId];
		if (userId) {
			addSubscription(
				identifier,
				[
					{
						context: "onCreateNotification",
						query: onCreateNotificationSubscription,
						variables: {
							userId,
						},
					},
					{
						context: "onUpdateNewNotification",
						query: onUpdateNewNotificationSubscription,
						variables: {
							userId,
						},
					},
					{
						context: "onUpdateNotification",
						query: onUpdateNotificationSubscription,
						variables: {
							userId,
						},
					},
				],
				true
			);
		}

		return () => {
			deleteSubscription(identifier);
		};
	}, [userId, addSubscription, deleteSubscription]);
};
